<template>
  <div class="conseiller-page client-page">
    <header class="client-page__header">
      <h1 class="client-page__title">Échanger avec un conseiller</h1>
      <p class="client-page__intro">
        Posez vos questions sur votre dossier, vos résultats ou vos prochaines étapes.
      </p>
    </header>

    <div class="conseiller-layout">
      <!-- Colonne principale -->
      <div class="conseiller-layout__main">
        <!-- Formulaire message -->
        <section class="client-card conseiller-form-card">
          <h2 class="client-section__label">Votre message</h2>

          <p v-if="loadingEmployes" class="conseiller-page__state">Chargement des conseillers…</p>
          <div v-else-if="employes.length === 0" class="conseiller-page__state conseiller-page__state--error">
            <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
            {{ errorMessage || 'Aucun conseiller disponible pour le moment.' }}
          </div>
          <template v-else>
            <div class="form-group">
              <label for="advisor" class="form-label">Conseiller</label>
              <select id="advisor" v-model="selectedEmployeId" class="form-input form-input--select">
                <option v-for="e in employes" :key="e.id" :value="e.id">
                  {{ e.prenom }} {{ e.nom }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="subject" class="form-label">Objet</label>
              <select id="subject" v-model="message.subject" class="form-input form-input--select">
                <option value="">Objet — choisir un sujet</option>
                <option value="Mon analyse patrimoniale">Mon analyse patrimoniale</option>
                <option value="Question patrimoniale">Question patrimoniale</option>
                <option value="Modification de dossier">Modification de dossier</option>
                <option value="Document ou justificatif">Document ou justificatif</option>
                <option value="Rendez-vous">Rendez-vous</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div class="form-group">
              <label for="messageContent" class="form-label">Message</label>
              <textarea
                id="messageContent"
                v-model="message.content"
                class="form-input form-input--textarea"
                rows="5"
                placeholder="Décrivez votre question…"
                maxlength="2000"
                @input="updateCharCount"
              />
              <p class="form-hint">
                <span :class="{ 'form-hint--warn': charCount >= 1900 }">{{ charCount }} / 2000</span>
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Pièce jointe (optionnel)</label>
              <label class="form-file">
                <input
                  type="file"
                  class="form-file__input"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  @change="onFileSelect"
                />
                <span class="form-file__label">
                  <PaperClipIcon class="w-4 h-4 shrink-0" />
                  {{ attachmentName || 'Ajouter un document (optionnel)' }}
                </span>
              </label>
            </div>

            <div v-if="formError" class="form-error">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              {{ formError }}
            </div>
            <div v-if="successSent" class="form-success">
              <CheckCircleIcon class="w-5 h-5 shrink-0" />
              Message envoyé. Votre conseiller vous répondra sous 24&nbsp;h ouvrées.
            </div>

            <button
              type="button"
              class="form-submit"
              :disabled="isSending || !message.content.trim() || !selectedEmploye"
              @click="sendMessage"
            >
              <span v-if="!isSending">Envoyer mon message</span>
              <span v-else class="form-submit__spinner" aria-hidden="true" />
            </button>
            <p class="form-footnote">
              Réponse sous 24&nbsp;h ouvrées · lundi–vendredi, 9&nbsp;h–18&nbsp;h
            </p>
          </template>
        </section>

        <!-- Historique -->
        <section class="client-card conseiller-history-card">
          <h2 class="client-section__label">Vos échanges</h2>
          <p v-if="loadingMessages" class="conseiller-page__state">Chargement de l’historique…</p>
          <p v-else-if="!messages.length" class="conseiller-page__state">
            Aucun échange pour le moment. Envoyez votre premier message ci-dessus.
          </p>
          <ul v-else class="exchange-list">
            <li v-for="m in messages" :key="m.id" class="exchange-item">
              <span class="exchange-badge" :class="messageBadgeClass(m.statut)">
                {{ messageBadgeLabel(m.statut) }}
              </span>
              <div class="exchange-item__body">
                <p class="exchange-item__title">{{ m.subject || 'Sans objet' }}</p>
                <p class="exchange-item__meta">
                  <template v-if="m.employe_name">{{ m.employe_name }} — </template>
                  {{ formatDate(m.created_at) }}
                </p>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- Rail droit -->
      <aside class="conseiller-layout__rail">
        <section v-if="selectedEmploye" class="advisor-hero">
          <div class="advisor-hero__avatar">{{ getInitials(selectedEmploye) }}</div>
          <h2 class="advisor-hero__name">{{ selectedEmploye.prenom }} {{ selectedEmploye.nom }}</h2>
          <p class="advisor-hero__meta">
            Conseiller patrimonial
            <span class="advisor-hero__dot" aria-hidden="true" />
            Répond sous 24&nbsp;h
          </p>
          <div class="advisor-hero__actions">
            <button type="button" class="advisor-hero__btn advisor-hero__btn--solid" @click="prefillRdv">
              Prendre rendez-vous
            </button>
            <a
              v-if="advisorTelHref"
              :href="advisorTelHref"
              class="advisor-hero__btn advisor-hero__btn--outline"
            >
              Appeler
            </a>
            <a
              v-else-if="selectedEmploye.email"
              :href="'mailto:' + selectedEmploye.email"
              class="advisor-hero__btn advisor-hero__btn--outline"
            >
              Écrire
            </a>
          </div>
        </section>

        <section class="client-card">
          <h2 class="client-section__label">Questions fréquentes</h2>
          <ul class="faq-list">
            <li v-for="(item, idx) in faqItems" :key="item.q" class="faq-item">
              <button
                type="button"
                class="faq-item__trigger"
                :aria-expanded="openFaq === idx"
                @click="openFaq = openFaq === idx ? null : idx"
              >
                <span>{{ item.q }}</span>
                <span class="faq-item__icon" aria-hidden="true">{{ openFaq === idx ? '−' : '+' }}</span>
              </button>
              <p v-if="openFaq === idx" class="faq-item__answer">{{ item.a }}</p>
            </li>
          </ul>
        </section>

        <section class="client-card">
          <h2 class="client-section__label">Autres contacts</h2>
          <ul class="other-contacts">
            <li>
              <span class="other-contacts__label">Administratif</span>
              <a :href="'mailto:' + contactEmails.general">{{ contactEmails.general }}</a>
            </li>
            <li>
              <span class="other-contacts__label">Support technique</span>
              <a :href="'mailto:' + contactEmails.support">{{ contactEmails.support }}</a>
            </li>
          </ul>
          <router-link to="/analyse-patrimoniale/aide" class="aide-link-btn">
            Voir l’aide
          </router-link>
        </section>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  PaperClipIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { getUserRole, isClientFacingRole } from '@/utils/auth';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { syncEmployeProfileFromApi } from '@/utils/permissions';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';

const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
const messageSubmitGuard = createSubmitGuard(2500);

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const router = useRouter();
const contactEmails = {
  support: 'support@patrimoneabygwem.com',
  general: 'contact@patrimoneabygwem.com',
};

const faqItems = [
  {
    q: 'Quand aurai-je mon analyse ?',
    a: 'Après soumission du questionnaire, comptez environ 72 h ouvrées. Vous recevez un email dès la publication, avec un code pour consulter vos résultats.',
  },
  {
    q: 'Puis-je changer de conseiller ?',
    a: 'Vous pouvez écrire à n’importe quel conseiller listé sur cette page. Pour un changement durable d’interlocuteur, contactez l’administratif.',
  },
  {
    q: 'Mes données sont-elles protégées ?',
    a: 'Oui. L’accès à vos bilans est protégé par un code à usage contrôlé. Les échanges passent par l’espace sécurisé Patrimonea.',
  },
];

const employes = ref([]);
const loadingEmployes = ref(false);
const selectedEmployeId = ref(null);
const message = ref({ subject: '', content: '' });
const charCount = ref(0);
const attachmentFile = ref(null);
const attachmentName = ref('');
const isSending = ref(false);
const successSent = ref(false);
const formError = ref('');
const errorMessage = ref('');
const messages = ref([]);
const loadingMessages = ref(false);
const openFaq = ref(null);

const selectedEmploye = computed(() => {
  const id = Number(selectedEmployeId.value);
  return employes.value.find((e) => Number(e.id) === id) || null;
});

const advisorTelHref = computed(() => {
  const tel = String(selectedEmploye.value?.tel || '').replace(/\s+/g, '');
  return tel ? `tel:${tel}` : '';
});

watch(employes, (list) => {
  if (list.length && !selectedEmployeId.value) {
    selectedEmployeId.value = list[0].id;
  }
});

const updateCharCount = () => {
  charCount.value = (message.value.content || '').length;
};

function getInitials(employe) {
  const p = (employe.prenom || '').trim();
  const n = (employe.nom || '').trim();
  return ((p[0] || '') + (n[0] || '')).toUpperCase() || '?';
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch (_) {
    return '';
  }
}

function messageBadgeLabel(statut) {
  const s = String(statut || 'entame').toLowerCase();
  if (s === 'resolu') return 'Répondu';
  if (s === 'bloque') return 'En cours';
  return 'Envoyé';
}

function messageBadgeClass(statut) {
  const s = String(statut || 'entame').toLowerCase();
  if (s === 'resolu') return 'exchange-badge--ok';
  if (s === 'bloque') return 'exchange-badge--warn';
  return 'exchange-badge--sent';
}

function prefillRdv() {
  message.value.subject = 'Rendez-vous';
  successSent.value = false;
  formError.value = '';
  const el = document.getElementById('messageContent');
  if (el) el.focus();
}

function onFileSelect(e) {
  const f = e.target.files?.[0];
  attachmentFile.value = f || null;
  attachmentName.value = f ? f.name : '';
}

async function sendMessage() {
  if (!selectedEmploye.value || !message.value.content.trim()) {
    formError.value = 'Veuillez écrire un message.';
    return;
  }
  formError.value = '';
  successSent.value = false;
  if (attachmentFile.value && attachmentFile.value.size > ATTACHMENT_MAX_BYTES) {
    formError.value = 'Pièce jointe trop volumineuse (max 5 Mo).';
    return;
  }
  const guarded = await messageSubmitGuard.run(async () => {
    isSending.value = true;
    try {
      const payload = {
        employe_id: selectedEmploye.value.id,
        subject: message.value.subject.trim() || 'Message depuis l’application',
        content: message.value.content.trim().slice(0, 2000),
      };
      if (attachmentFile.value) {
        payload.attachment_base64 = await readFileAsBase64(attachmentFile.value);
        payload.attachment_filename = attachmentFile.value.name;
        payload.attachment_content_type =
          attachmentFile.value.type || 'application/octet-stream';
      }
      await axios.post('/api/send-message/', payload, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      });
      successSent.value = true;
      message.value = { subject: message.value.subject, content: '' };
      charCount.value = 0;
      attachmentFile.value = null;
      attachmentName.value = '';
      await fetchMessages();
    } catch (error) {
      formError.value =
        formatThrottleMessage(error) ||
        (error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : error.response?.data?.error || 'Erreur lors de l’envoi du message.');
      if (error.response?.status === 401) {
        formError.value = 'Session expirée, veuillez vous reconnecter.';
        router.push('/connexion');
      }
    } finally {
      isSending.value = false;
    }
  });
  if (!guarded.ok) {
    formError.value = 'Veuillez patienter avant d’envoyer un autre message.';
  }
}

async function fetchEmployes() {
  loadingEmployes.value = true;
  try {
    const role = (getUserRole() || '').toString().trim().toLowerCase();
    if (role && !isClientFacingRole(role)) return;

    if (!isAuthenticatedSession()) {
      router.push('/connexion');
      return;
    }
    const response = await axios.get('/api/conseillers/', {
      withCredentials: true,
    });
    const raw = response.data;
    employes.value = Array.isArray(raw) ? raw : raw?.results || raw?.data || [];
  } catch (error) {
    const status = error.response?.status;
    if (status === 401) router.push('/connexion');
    if (status === 403) {
      employes.value = [];
      errorMessage.value =
        'Accès refusé. Votre profil ne permet pas d’afficher la liste des conseillers pour le moment.';
    }
  } finally {
    loadingEmployes.value = false;
  }
}

async function fetchMessages() {
  loadingMessages.value = true;
  try {
    if (!isAuthenticatedSession()) return;
    const { data } = await axios.get('/api/messages/', { withCredentials: true });
    const list = Array.isArray(data) ? data : data?.results || [];
    messages.value = list
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } catch (_) {
    messages.value = [];
  } finally {
    loadingMessages.value = false;
  }
}

onMounted(async () => {
  await syncEmployeProfileFromApi();
  await Promise.all([fetchEmployes(), fetchMessages()]);
});
</script>

<style scoped>
.conseiller-page {
  min-height: 100%;
  font-family: var(--font-body);
  padding: 0 0 3rem;
}

.conseiller-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-5);
  align-items: start;
}

@media (min-width: 1040px) {
  .conseiller-layout {
    grid-template-columns: minmax(0, 1.6fr) minmax(260px, 320px);
  }
}

.conseiller-layout__main,
.conseiller-layout__rail {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
}

@media (min-width: 1040px) {
  .conseiller-layout__rail {
    position: sticky;
    top: var(--space-4);
  }
}

.conseiller-page__state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: var(--client-text-muted);
  margin: 0;
}

.conseiller-page__state--error {
  color: var(--brand-red);
}

.form-group {
  margin-bottom: 1.1rem;
}

.form-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--brand-navy);
  margin-bottom: 0.4rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-family: var(--font-body);
  color: var(--brand-ink);
  background: var(--paper);
  border: 1px solid var(--client-line);
  border-radius: var(--radius-field);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--brand-navy);
  box-shadow: 0 0 0 3px rgba(1, 38, 62, 0.1);
}

.form-input--select {
  cursor: pointer;
  appearance: auto;
}

.form-input--textarea {
  resize: vertical;
  min-height: 140px;
}

.form-hint {
  font-size: 0.75rem;
  color: var(--muted);
  margin: 0.35rem 0 0;
}

.form-hint--warn {
  color: var(--brand-gold-dark);
}

.form-file {
  position: relative;
  display: block;
  cursor: pointer;
}

.form-file__input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.form-file__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--client-text-muted);
  background: var(--paper);
  border: 1px solid var(--client-line);
  border-radius: var(--radius-field);
}

.form-file:hover .form-file__label {
  border-color: var(--brand-gold);
}

.form-error,
.form-success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  border-radius: var(--radius-field);
}

.form-error {
  color: var(--brand-red);
  background: var(--brand-red-soft);
  border: 1px solid color-mix(in srgb, var(--brand-red) 25%, transparent);
}

.form-success {
  color: var(--success);
  background: color-mix(in srgb, var(--success) 12%, #fff);
  border: 1px solid color-mix(in srgb, var(--success) 25%, transparent);
}

.form-submit {
  width: 100%;
  padding: 0.9rem 1.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: var(--font-body);
  color: var(--brand-navy);
  background: var(--brand-gold);
  border: 1px solid var(--brand-gold);
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background 0.2s;
  min-height: 48px;
}

.form-submit:hover:not(:disabled) {
  background: var(--brand-gold-light);
  border-color: var(--brand-gold-light);
}

.form-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-submit__spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid rgba(1, 38, 62, 0.2);
  border-top-color: var(--brand-navy);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.form-footnote {
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: var(--client-text-muted);
  text-align: center;
}

.exchange-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.exchange-item {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 0.9rem 0;
  border-bottom: 1px solid var(--client-line);
}

.exchange-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.exchange-item:first-child {
  padding-top: 0;
}

.exchange-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-pill);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.exchange-badge--ok {
  background: color-mix(in srgb, var(--success) 14%, #fff);
  color: var(--success);
}

.exchange-badge--warn {
  background: var(--brand-gold-soft);
  color: var(--brand-gold-dark);
}

.exchange-badge--sent {
  background: rgba(1, 38, 62, 0.06);
  color: var(--brand-navy);
}

.exchange-item__title {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-navy);
}

.exchange-item__meta {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--client-text-muted);
}

.advisor-hero {
  background: var(--brand-navy);
  color: var(--paper);
  border-radius: var(--client-radius-lg, 24px);
  padding: var(--space-5);
  box-shadow: var(--shadow-md);
  text-align: center;
}

.advisor-hero__avatar {
  width: 64px;
  height: 64px;
  margin: 0 auto var(--space-3);
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--brand-gold) 55%, #fff);
  color: var(--brand-navy);
  font-weight: 700;
  font-size: 1.1rem;
}

.advisor-hero__name {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--paper);
}

.advisor-hero__meta {
  margin: 0.4rem 0 var(--space-4);
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.advisor-hero__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(45, 134, 89, 0.25);
}

.advisor-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.advisor-hero__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.65rem 1rem;
  border-radius: var(--radius-pill);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.advisor-hero__btn--solid {
  background: var(--paper);
  color: var(--brand-navy);
  border: 1px solid var(--paper);
}

.advisor-hero__btn--solid:hover {
  background: #f3f4f6;
}

.advisor-hero__btn--outline {
  background: transparent;
  color: var(--paper);
  border: 1px solid rgba(255, 255, 255, 0.45);
}

.advisor-hero__btn--outline:hover {
  background: rgba(255, 255, 255, 0.08);
}

.faq-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.faq-item {
  border-bottom: 1px solid var(--client-line);
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-item__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 0;
  background: none;
  border: none;
  text-align: left;
  font-family: var(--font-body);
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--brand-navy);
  cursor: pointer;
}

.faq-item__icon {
  font-size: 1.1rem;
  color: var(--client-text-muted);
  line-height: 1;
}

.faq-item__answer {
  margin: 0 0 0.85rem;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--client-text-muted);
}

.other-contacts {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.other-contacts li {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.other-contacts__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--brand-navy);
}

.other-contacts a {
  font-size: 0.8125rem;
  color: var(--client-text-muted);
  text-decoration: none;
  word-break: break-all;
}

.other-contacts a:hover {
  color: var(--brand-gold-dark);
  text-decoration: underline;
}

.aide-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  width: 100%;
  margin-top: var(--space-4, 1rem);
  padding: 0.65rem 1rem;
  border-radius: var(--radius-pill);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  color: var(--brand-navy);
  background: transparent;
  border: 1px solid var(--client-line);
  transition: background 0.15s ease, border-color 0.15s ease;
}

.aide-link-btn:hover {
  background: rgba(1, 38, 62, 0.04);
  border-color: var(--brand-gold);
}
</style>
